// P3-025: Bottom agent team display
// 5 slots with avatar placeholder, type color border, ATK value,
// skill CD countdown, flash on skill trigger, grey out on overload disable

import { useEffect, useState, useCallback } from 'react';
import { AgentType } from '@/shared/types/common';
import { useBattleStore } from '../stores/battleStore';
import { EventBus } from '@/shared/game/EventBus';
import type { BattleAgent } from '../types/battle-internal';

/** Agent type to CSS color */
const TYPE_COLORS: Record<AgentType, string> = {
  [AgentType.BACKEND]: 'var(--color-element-red)',
  [AgentType.FRONTEND]: 'var(--color-element-blue)',
  [AgentType.TESTING]: 'var(--color-element-green)',
  [AgentType.DEVOPS]: 'var(--color-element-yellow)',
  [AgentType.HACKER]: 'var(--color-element-purple)',
};

/** Agent type short labels */
const TYPE_LABELS: Record<AgentType, string> = {
  [AgentType.BACKEND]: 'Backend',
  [AgentType.FRONTEND]: 'Frontend',
  [AgentType.TESTING]: 'Testing',
  [AgentType.DEVOPS]: 'DevOps',
  [AgentType.HACKER]: 'Hacker',
};

/** Agent type abbreviations for avatar */
const TYPE_ABBREV: Record<AgentType, string> = {
  [AgentType.BACKEND]: 'BE',
  [AgentType.FRONTEND]: 'FE',
  [AgentType.TESTING]: 'QA',
  [AgentType.DEVOPS]: 'OP',
  [AgentType.HACKER]: 'HK',
};

export function AgentTeamBar() {
  const team = useBattleStore((s) => s.team);

  // Track which agents just had skills triggered (for flash animation)
  const [flashingAgents, setFlashingAgents] = useState<Set<string>>(new Set());

  // Track which agents are disabled by overload
  const [disabledAgents, setDisabledAgents] = useState<Set<string>>(new Set());

  // Listen for skill trigger events
  useEffect(() => {
    const handleSkillTriggered = (data: { agentId: string; skillName: string }) => {
      setFlashingAgents((prev) => {
        const next = new Set(prev);
        next.add(data.agentId);
        return next;
      });

      // Remove flash after animation
      setTimeout(() => {
        setFlashingAgents((prev) => {
          const next = new Set(prev);
          next.delete(data.agentId);
          return next;
        });
      }, 500);
    };

    const handleOverload = (data: { affectedAgentIds: string[] }) => {
      setDisabledAgents(new Set(data.affectedAgentIds));
    };

    const handleTurnEnd = () => {
      // Clear disabled state at turn end (re-enabled at turn start)
      setDisabledAgents(new Set());
    };

    EventBus.on('skillTriggered', handleSkillTriggered);
    EventBus.on('overloadTriggered', handleOverload);
    EventBus.on('turnEnd', handleTurnEnd);

    return () => {
      EventBus.off('skillTriggered', handleSkillTriggered);
      EventBus.off('overloadTriggered', handleOverload);
      EventBus.off('turnEnd', handleTurnEnd);
    };
  }, []);

  const renderSlot = useCallback(
    (agent: BattleAgent, index: number) => {
      const isFlashing = flashingAgents.has(agent.agentId);
      const isDisabled = disabledAgents.has(agent.agentId) || agent.skillDisabledThisTurn;
      const borderColor = TYPE_COLORS[agent.type] ?? 'var(--border-color)';
      const isReady = agent.currentSkillCD === 0 && !isDisabled;

      const slotClasses = [
        'agent-slot',
        isReady ? 'agent-slot--active' : '',
        isDisabled ? 'agent-slot--disabled' : '',
        isFlashing ? 'agent-slot--skill-flash' : '',
      ]
        .filter(Boolean)
        .join(' ');

      return (
        <div key={agent.agentId || index} className={slotClasses} style={{ borderColor }}>
          {/* Avatar placeholder */}
          <div className="agent-slot__avatar" style={{ color: borderColor }}>
            {TYPE_ABBREV[agent.type] ?? '??'}
          </div>

          {/* Type label */}
          <span className="agent-slot__type" style={{ color: borderColor }}>
            {TYPE_LABELS[agent.type] ?? agent.type}
          </span>

          {/* ATK value */}
          <span className="agent-slot__atk">ATK:{Math.round(agent.attack)}</span>

          {/* Skill CD */}
          <span
            className={`agent-slot__skill-cd ${
              isReady ? 'agent-slot__skill-cd--ready' : 'agent-slot__skill-cd--cooling'
            }`}
          >
            {isReady ? 'READY!' : `CD:${agent.currentSkillCD}`}
          </span>

          {/* Disabled icon */}
          {isDisabled && <span className="agent-slot__disabled-icon">!</span>}
        </div>
      );
    },
    [flashingAgents, disabledAgents],
  );

  return (
    <div className="agent-team-bar">
      {team.map((agent, idx) => renderSlot(agent, idx))}

      {/* Fill empty slots if team has fewer than 5 */}
      {Array.from({ length: Math.max(0, 5 - team.length) }).map((_, idx) => (
        <div key={`empty-${idx}`} className="agent-slot" style={{ opacity: 0.3 }}>
          <div className="agent-slot__avatar">--</div>
          <span className="agent-slot__type" style={{ color: 'var(--color-text-muted)' }}>
            Empty
          </span>
        </div>
      ))}
    </div>
  );
}
